import { useEffect, useMemo, useRef, useState } from 'react'
import { hidePurchases } from '../lib/native'
import { Link } from 'react-router-dom'
import type { Plan } from '../lib/types'

/* ─────────────────────────────────────────────────────────────
   The Radiant Gate — Family Ancestry's animated front door.
   Parallax starfields, shooting stars, a living constellation,
   scroll-choreographed sections, 3D tilt cards, a self-playing
   Sage demo, and a marquee of the family lexicon.
   ───────────────────────────────────────────────────────────── */

const FEATURES = [
  { icon: '🕸️', title: 'Graph Constellation View', body: 'Your family is not a list — it is a living graph. Generations rank themselves, unions bind in gold, bloodlines descend in violet.' },
  { icon: '🗺️', title: 'The Family Atlas', body: 'Every birthplace, every resting place, plotted on a real map. Watch the migration of your name across continents and centuries.' },
  { icon: '🧙', title: 'Sage — an agent, not a chatbot', body: '“Add my grandmother Rosa, born Naples 1932.” Done — person created, dates parsed, receipts shown. Voice or text, it acts on your word.' },
  { icon: '🔐', title: 'The Legacy Vault (E2EE)', body: 'True end-to-end encryption for chosen photos, videos, and documents. Keys are derived on your device — the server holds only starlight and ciphertext.' },
  { icon: '🕊️', title: 'Path to Paradise', body: 'Cemetery, lot number, resting-place notes — so no grave is ever lost to memory, and every pilgrimage finds its way.' },
  { icon: '📅', title: 'Meet-Up Calendar', body: 'Reunions with RSVPs, birthdays of the living, remembrance days of the departed — woven automatically from the tree itself.' },
]

const STEPS = [
  { n: 1, icon: '🌱', title: 'Plant a name', body: 'Start with yourself or the oldest ancestor you know. One name is enough — the sky fills from there.' },
  { n: 2, icon: '✨', title: 'Let it constellate', body: 'Add parents, spouses, children — or just tell Sage. The graph engine arranges every generation on its own.' },
  { n: 3, icon: '🫂', title: 'Gather the living', body: 'Share trees with kin, plan meet-ups, seal treasures in the vault. The archive becomes a family place.' },
]

const MARQUEE_WORDS = ['constellations', 'bloodlines', 'unions', 'birthplaces', 'reunions', 'maiden names', 'the old country', 'lot 22-C', 'remembrance', 'first words', 'immigration records', 'the family recipe', 'grandmother’s garden', 'a name reclaimed']

const DEMO_SCRIPT: { role: 'user' | 'sage'; text: string; receipt?: string }[] = [
  { role: 'user', text: 'Add my grandfather Henry, born June 1928 in Boston. He rests at Oakwood Cemetery, lot 22-C.' },
  { role: 'sage', text: 'Henry joins the constellation — birth recorded, and his Path to Paradise is marked so the family can always find him.', receipt: '⚡ Added Henry · burial: Oakwood Cemetery, Lot 22-C' },
  { role: 'user', text: 'Plan a memorial gathering there on September 5th and invite everyone.' },
  { role: 'sage', text: 'Scheduled — the ravens are out. Everyone on this tree just received the invitation.', receipt: '⚡ Meet-up created · 📨 4 kin notified' },
]

function seededStars(n: number, seed: number) {
  let s = seed
  const rnd = () => { s = (s * 16807) % 2147483647; return s / 2147483647 }
  return Array.from({ length: n }, (_, i) => ({
    id: i, left: rnd() * 100, top: rnd() * 100,
    size: 0.8 + rnd() * 2.4, delay: rnd() * 5,
  }))
}

function useReveal(deps: unknown[] = []) {
  useEffect(() => {
    // re-scan on dep changes so late-rendered elements (e.g. fetched pricing cards) get observed
    const els = document.querySelectorAll('.reveal:not(.is-in), .reveal-scale:not(.is-in)')
    const io = new IntersectionObserver(entries => {
      for (const e of entries) if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target) }
    }, { threshold: 0.15 })
    els.forEach(el => io.observe(el))
    return () => io.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

function TiltCard({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const onMove = (e: React.MouseEvent) => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const px = (e.clientX - r.left) / r.width - 0.5
    const py = (e.clientY - r.top) / r.height - 0.5
    el.style.transform = `perspective(900px) rotateY(${px * 10}deg) rotateX(${py * -10}deg) translateY(-4px)`
  }
  const onLeave = () => { if (ref.current) ref.current.style.transform = '' }
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}
      className={`tilt-card ${className || ''}`}>
      {children}
    </div>
  )
}

function SageDemo() {
  const [visible, setVisible] = useState(0)
  const [typing, setTyping] = useState(false)
  useEffect(() => {
    const tick = () => {
      setVisible(v => {
        if (v >= DEMO_SCRIPT.length) { return 0 }
        return v + 1
      })
    }
    setTyping(true)
    const iv = setInterval(() => { setTyping(t => !t); tick() }, 2600)
    return () => clearInterval(iv)
  }, [])
  return (
    <div className="card-glass !bg-void-900/80 p-5 max-w-md mx-auto text-left shadow-2xl shadow-rootv-600/10">
      <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-white/[0.08]">
        <span className="w-9 h-9 rounded-full bg-gradient-to-br from-gold-500 to-rootv-600 flex items-center justify-center text-lg sage-pulse">🧙</span>
        <div>
          <p className="font-display font-semibold text-sm text-slate-100">Sage</p>
          <p className="text-[10px] text-emerald-400">● watching “The Ashford Line”</p>
        </div>
      </div>
      <div className="space-y-3 min-h-[280px]">
        {DEMO_SCRIPT.slice(0, visible).map((m, i) => (
          <div key={`${i}-${visible <= i ? 'h' : 'v'}`} className={`demo-msg ${m.role === 'user' ? 'flex justify-end' : ''}`} style={{ animationDelay: '0.05s' }}>
            <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
              m.role === 'user' ? 'bg-gold-500/15 border border-gold-500/25 text-slate-100' : 'bg-white/[0.05] border border-white/[0.08] text-slate-200'}`}>
              {m.text}
              {m.receipt && (
                <div className="mt-2 text-[11px] px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-emerald-300">{m.receipt}</div>
              )}
            </div>
          </div>
        ))}
        {visible < DEMO_SCRIPT.length && typing && (
          <div className="flex gap-1.5 px-2">
            <span className="typing-dot w-2 h-2 rounded-full bg-gold-400" />
            <span className="typing-dot w-2 h-2 rounded-full bg-gold-400" />
            <span className="typing-dot w-2 h-2 rounded-full bg-gold-400" />
          </div>
        )}
      </div>
    </div>
  )
}

export default function Landing() {
  const [plans, setPlans] = useState<Plan[]>([])
  const layer1 = useMemo(() => seededStars(70, 7), [])
  const layer2 = useMemo(() => seededStars(45, 99), [])
  const layer3 = useMemo(() => seededStars(24, 1234), [])
  useReveal([plans])

  useEffect(() => {
    // via the api client (handles the v0.8.0 {plans,...} envelope) — raw fetch here
    // crashed the landing when the response shape changed (v0.8.1 fix, D-047)
    import('../lib/api').then(({ api }) => api.listPlans().then(setPlans)).catch(() => {})
  }, [])

  return (
    <div className="min-h-screen bg-void-950 text-slate-200 overflow-x-hidden" style={{ scrollBehavior: 'smooth' }}>
      {/* ── Nav ── */}
      <header className="absolute top-0 inset-x-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="text-2xl float-slow inline-block">🌳</span>
            <span className="font-display font-semibold text-lg text-gradient-gold">Family Ancestry</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="#how" className="text-sm text-slate-400 hover:text-gold-300 transition-colors hidden md:inline">How it works</a>
            <a href="#sage" className="text-sm text-slate-400 hover:text-gold-300 transition-colors hidden md:inline">Sage</a>
            <a href="#pricing" className="text-sm text-slate-400 hover:text-gold-300 transition-colors hidden sm:inline">Pricing</a>
            <Link to="/login" className="btn-gold btn-shine !py-2 text-sm">Sign In</Link>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative min-h-[100svh] flex items-center justify-center">
        {/* parallax starfields */}
        <div className="star-layer star-drift-1">
          {layer1.map(st => <span key={st.id} className="star" style={{ left: `${st.left}%`, top: `${st.top}%`, width: st.size, height: st.size, animationDelay: `${st.delay}s` }} />)}
        </div>
        <div className="star-layer star-drift-2">
          {layer2.map(st => <span key={st.id} className="star" style={{ left: `${st.left}%`, top: `${st.top}%`, width: st.size * 1.4, height: st.size * 1.4, animationDelay: `${st.delay}s`, opacity: 0.8 }} />)}
        </div>
        <div className="star-layer star-drift-3">
          {layer3.map(st => <span key={st.id} className="star" style={{ left: `${st.left}%`, top: `${st.top}%`, width: st.size * 1.9, height: st.size * 1.9, animationDelay: `${st.delay}s` }} />)}
        </div>
        {/* shooting stars */}
        <span className="shooting-star" style={{ top: '12%', left: '72%', animationDelay: '1.5s' }} />
        <span className="shooting-star" style={{ top: '28%', left: '38%', animationDelay: '4.2s' }} />
        <span className="shooting-star" style={{ top: '8%', left: '22%', animationDelay: '9s' }} />
        {/* auroras */}
        <div className="aurora aurora-gold aurora-drift w-[520px] h-[520px] top-[6%] left-[6%]" />
        <div className="aurora aurora-violet aurora-drift-2 w-[460px] h-[460px] bottom-[10%] right-[4%]" />
        <div className="aurora aurora-teal aurora-drift w-[320px] h-[320px] top-[52%] left-[44%]" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-24 pb-16">
          <p className="fade-up text-gold-400 tracking-[0.35em] text-xs font-semibold uppercase mb-6">
            ✳ every family is a constellation ✳
          </p>
          <h1 className="fade-up-1 font-display text-5xl sm:text-7xl font-bold leading-[1.04] mb-6">
            Your lineage,<br />
            <span className="shimmer-text">drawn in starlight.</span>
          </h1>
          <p className="fade-up-2 text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10">
            Map generations as a living graph. Watch unions bind, bloodlines descend, and forgotten
            names find their place — with an AI genealogist who doesn't just answer. <span className="text-gold-300">It acts.</span>
          </p>
          <div className="fade-up-3 flex items-center justify-center gap-4 flex-wrap">
            <Link to="/login" className="btn-gold btn-shine text-base !px-8 !py-3.5">Begin Your Constellation</Link>
            <a href="#how" className="btn-ghost text-base !px-8 !py-3.5">See the magic ↓</a>
          </div>

          {/* living constellation */}
          <svg viewBox="0 0 640 300" className="mt-14 mx-auto w-full max-w-2xl" aria-hidden>
            <g stroke="#d9a441" strokeWidth="1.4" fill="none" opacity="0.7">
              <path className="root-line" d="M200 60 H320" />
              <path className="root-line" d="M260 60 V115 M260 115 H140 M260 115 H400 M140 115 V160 M400 115 V160" style={{ animationDelay: '0.5s' }} />
              <path className="root-line" d="M140 192 V210 M400 192 V210 M140 210 H80 M140 210 H210 M80 210 V235 M210 210 V235" style={{ animationDelay: '1.1s' }} />
              <path className="root-line" d="M440 160 H360" style={{ animationDelay: '1s' }} />
              <path className="root-line" d="M270 235 V262 M80 262 H270" style={{ animationDelay: '1.7s' }} />
            </g>
            {[[200, 60], [320, 60], [140, 176], [400, 176], [440, 160, 1], [80, 250], [210, 250], [270, 276, 1]].map(([x, y, alt], i) => (
              <g key={i}>
                <circle cx={x} cy={y} r="15" fill={alt ? '#7c3aed' : '#d9a441'} opacity="0.14" className="node-pulse" />
                <circle cx={x} cy={y} r="5.5" fill={alt ? '#a78bfa' : '#e8bc6a'} />
              </g>
            ))}
            {/* orbiting sparks */}
            <g className="orbiter"><circle cx="320" cy="70" r="2.5" fill="#f3d59a" opacity="0.9" /></g>
            <g className="orbiter-2"><circle cx="260" cy="200" r="2" fill="#a78bfa" opacity="0.9" /></g>
          </svg>
        </div>

        {/* scroll cue */}
        <a href="#how" className="absolute bottom-6 left-1/2 -translate-x-1/2 text-slate-500 text-2xl float-slow" aria-label="scroll">⌄</a>
      </section>

      {/* ── Marquee ribbon ── */}
      <div className="marquee border-y border-white/[0.06] py-4 bg-void-900/50">
        <div className="marquee-track">
          {[...MARQUEE_WORDS, ...MARQUEE_WORDS].map((w, i) => (
            <span key={i} className="font-display text-lg text-slate-500 whitespace-nowrap">
              <span className="text-gold-500/70 mr-2.5">✳</span>{w}
            </span>
          ))}
        </div>
      </div>

      {/* ── How it works ── */}
      <section id="how" className="relative py-28">
        <div className="aurora aurora-teal w-[380px] h-[380px] top-10 right-[8%] opacity-15" />
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="reveal font-display text-3xl sm:text-5xl font-bold text-center mb-4">
            Three steps to <span className="text-gradient-gold">forever</span>
          </h2>
          <p className="reveal text-slate-500 text-center max-w-xl mx-auto mb-16">No spreadsheets. No forms marathon. A name, a spark, a sky.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.n} className="reveal card-glass p-7 text-center relative overflow-visible" style={{ transitionDelay: `${i * 0.12}s` }}>
                <span className="step-badge absolute -top-4 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-gradient-to-br from-gold-500 to-gold-400 text-void-950 font-display font-bold flex items-center justify-center shadow-lg shadow-gold-500/30" style={{ animationDelay: `${0.3 + i * 0.15}s` }}>
                  {s.n}
                </span>
                <div className={`text-5xl mb-4 mt-3 inline-block ${i % 2 ? 'float-slower' : 'float-slow'}`}>{s.icon}</div>
                <h3 className="font-display font-semibold text-xl text-slate-100 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="relative py-24">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="reveal font-display text-3xl sm:text-5xl font-bold text-center mb-4">
            Built like a <span className="text-gradient-gold">graph engine</span>,<br className="hidden sm:block" /> felt like an heirloom
          </h2>
          <p className="reveal text-slate-500 text-center max-w-xl mx-auto mb-14">
            Agent-driven UI on a generation-ranked layout engine — in service of your grandmother's maiden name.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <TiltCard key={i} className="reveal-scale card-glass p-6 rounded-2xl">
                <div className="text-3xl mb-3">{f.icon}</div>
                <h3 className="font-display font-semibold text-lg text-slate-100 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.body}</p>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sage demo ── */}
      <section id="sage" className="relative py-28">
        <div className="aurora aurora-violet w-[440px] h-[440px] top-[20%] left-[6%] opacity-20" />
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal">
            <p className="text-gold-400 tracking-[0.3em] text-xs font-semibold uppercase mb-4">meet sage</p>
            <h2 className="font-display text-3xl sm:text-5xl font-bold mb-5 leading-tight">
              Speak, and the<br /><span className="shimmer-text">archive obeys.</span>
            </h2>
            <p className="text-slate-400 leading-relaxed mb-6">
              Sage is an agent with hands, not a chatbot with opinions. Describe a relative and they're added —
              dates parsed, bonds linked, burial recorded. Ask it to plan the reunion and the invitations are
              already flying. Every action runs under <em>your</em> authorization, with receipts.
            </p>
            <ul className="space-y-2.5 text-sm text-slate-300 mb-8">
              {['Voice or text — hands-free on the go', 'One-tap fixes for gaps it finds in your tree', 'Era-aware research hints: census, manifests, parish books', 'End-to-end action receipts — see exactly what it did'].map((t, i) => (
                <li key={i} className="flex items-start gap-2.5"><span className="text-gold-400 mt-0.5">✳</span>{t}</li>
              ))}
            </ul>
            <Link to="/login" className="btn-gold btn-shine !px-7 !py-3">Summon Sage</Link>
          </div>
          <div className="reveal-scale">
            <SageDemo />
          </div>
        </div>
      </section>

      {/* ── Pricing (hidden inside the iOS shell — Apple 3.1.1, D-093) ── */}
      {!hidePurchases() && (
      <section id="pricing" className="relative py-28">
        <div className="aurora aurora-gold w-[400px] h-[400px] top-0 left-1/2 -translate-x-1/2 opacity-15" />
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="reveal font-display text-3xl sm:text-5xl font-bold text-center mb-3">
            Roots are free. <span className="text-gradient-gold">Branches scale.</span>
          </h2>
          <p className="reveal text-slate-500 text-center mb-14">Founder pricing — locked forever for early constellations.</p>
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {plans.map((plan, i) => (
              <TiltCard key={plan.id}
                className={`reveal card-glass p-7 rounded-2xl flex flex-col relative ${plan.popular ? 'border-gold-500/40 shadow-2xl shadow-gold-500/10 md:-translate-y-3' : ''}`}>
                <div style={{ transitionDelay: `${i * 0.1}s` }}>
                  {plan.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-gold-500 to-gold-400 text-void-950 text-xs font-bold px-3 py-1 rounded-full sage-pulse">
                      MOST LOVED
                    </span>
                  )}
                  <h3 className="font-display text-xl font-semibold text-slate-100">{plan.name}</h3>
                  <p className="text-xs text-slate-500 mb-4">{plan.tagline}</p>
                  <div className="mb-6">
                    <span className="font-display text-4xl font-bold text-gradient-gold">${plan.price}</span>
                    <span className="text-slate-500 text-sm">/{plan.period}</span>
                  </div>
                  <ul className="space-y-2.5 text-sm text-slate-300">
                    {plan.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <span className="text-gold-400 mt-0.5">✳</span>{f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/login" className={`mt-7 block text-center ${plan.popular ? 'btn-gold btn-shine' : 'btn-ghost'}`}>
                    {plan.price === 0 ? 'Claim the Early-Access Special' : `Choose ${plan.name}`}
                  </Link>
                </div>
              </TiltCard>
            ))}
          </div>
          <p className="reveal text-center text-xs text-slate-600 mt-8">
            Founder accounts (joined before September 2026) keep Heritage at $6/mo and Dynasty at $19/mo — locked forever. Secure checkout by Stripe.
          </p>
        </div>
      </section>
      )}

      {/* ── Final CTA ── */}
      <section className="py-32 text-center relative overflow-hidden">
        <div className="aurora aurora-gold aurora-drift w-[420px] h-[420px] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-20" />
        <span className="shooting-star" style={{ top: '20%', left: '68%', animationDelay: '2s' }} />
        <h2 className="reveal relative font-display text-4xl sm:text-6xl font-bold mb-6 leading-tight">
          The stars remember.<br /><span className="shimmer-text">Now you will too.</span>
        </h2>
        <p className="reveal text-slate-500 mb-10 max-w-md mx-auto">Installable on any device. Private by default. Encrypted where it matters most.</p>
        <div className="reveal">
          <Link to="/login" className="btn-gold btn-shine text-lg !px-12 !py-4">Create Your First Tree</Link>
        </div>
      </section>

      <footer className="border-t border-white/[0.06] py-8 text-center">
        <svg viewBox="0 0 200 20" className="w-40 mx-auto mb-3 opacity-40" aria-hidden>
          <line x1="0" y1="10" x2="80" y2="10" stroke="#d9a441" strokeWidth="0.6" />
          <circle cx="100" cy="10" r="3" fill="#e8bc6a" />
          <line x1="120" y1="10" x2="200" y2="10" stroke="#7c3aed" strokeWidth="0.6" />
        </svg>
        <p className="text-xs text-slate-600">Family Ancestry &middot; family-ancestry.istayintech.com &middot; private beta</p>
        <p className="text-xs text-slate-600 mt-2"><Link to="/privacy" className="hover:text-gold-400">Privacy</Link> &middot; <Link to="/terms" className="hover:text-gold-400">Terms</Link> &middot; <Link to="/delete-account" className="hover:text-gold-400">Delete account</Link></p>
      </footer>
    </div>
  )
}
